// =============================================================================
// Global fetch interceptor for the Admin SPA. Imported once from main.tsx,
// it wraps window.fetch so every request:
//
//   1. Includes credentials (sends the bricklit-admin-session cookie).
//   2. On 401 — surfaces the in-app Login screen instead of silently bouncing
//      to Google. A session that expires mid-use reloads the SPA; the boot
//      auth gate (useAuthGate → GET /api/auth/me) then sees the 401 and
//      renders Login (email/password + Google), so the operator chooses how
//      to re-authenticate. (W11: previously this auto-redirected to
//      /api/auth/google/start, which hid the password login entirely.)
//
// Excludes /api/auth/* itself so the login probe / password POST / OAuth
// round-trip can complete and handle their own 401 inline.
// Player (Electron renderer) does NOT import this — it uses device-token
// auth via the preload bridge.
// =============================================================================

import { contentGroupHeaders } from './groupScope';
import { API_BASE } from './config';

const originalFetch = window.fetch.bind(window);
let handling401 = false;

// 401 새로고침은 탭마다 10초에 한 번까지만. 새로고침 뒤에도 같은 401이 나면(예: /api/auth/me는 200인데
// 다른 API가 401) 무한 반복으로 화면을 못 쓰고 서버에 요청이 쏟아진다. 새로고침을 건너야 기억하므로 sessionStorage.
const RELOAD_KEY = 'bricklit:auth-reload-at', RELOAD_COOLDOWN_MS = 10_000;
export function claimAuthReload(now = Date.now()): boolean {
  try {
    const elapsed = now - Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (elapsed >= 0 && elapsed < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch { /* 저장소를 못 쓰면 예전처럼 새로고침한다 */ }
  return true;
}

window.fetch = (input, init = {}) => {
  const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url, API_BASE);
  const method = init.method ?? (input instanceof Request ? input.method : 'GET');
  const headers = new Headers(init.headers ?? (input instanceof Request ? input.headers : undefined));
  if (url.origin === new URL(API_BASE).origin && method.toUpperCase() === 'POST' && /^\/api\/(media|playlists|notices|folders)(\/|$)/.test(url.pathname)) {
    for (const [name, value] of Object.entries(contentGroupHeaders())) if (!headers.has(name)) headers.set(name, value);
  }
  const merged: RequestInit = {
    credentials: 'include',
    ...init,
    headers,
  };
  return originalFetch(input, merged).then((res) => {
    if ((res.status === 401 || res.status === 428) && !handling401) {
      const urlString =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.toString()
            : input.url;
      // Don't react to auth endpoints themselves — the caller (Login probe,
      // password POST, OAuth callback) handles the 401 inline.
      if (!/\/api\/auth\//.test(urlString)) {
        handling401 = true;
        // Reload so the boot auth gate re-probes /api/auth/me, gets the 401,
        // and mounts the Login screen. Reloading (rather than mutating React
        // state from outside the tree) keeps a single source of truth and
        // works regardless of where in the app the stale-session 401 fired.
        if (claimAuthReload()) window.location.reload();
        else console.warn('[auth] 401 뒤 새로고침을 방금 했습니다 — 반복을 막으려 이번에는 건너뜁니다:', urlString);
      }
    }
    return res;
  });
};
